import { Alert, Button, Col, message, Row, Spin, Timeline } from 'antd';
import { UserAuthenticationContext } from 'components/context/UserAuthenticationProvider';
import React, { useContext, useState } from 'react';
import { createHikeEntry } from 'services/hitchit';
import { Hike } from 'services/hitchit/types/hike/Hike';
import { useQuery, useQueryClient } from 'react-query';
import { getTriggerData } from 'services/fetcher/hikeCalls';
import { useQueryResult } from 'components/utils/handleQueryResult';
import { TriggerData, TriggerDataResponse } from 'services/hitchit/types/usecases/OrderToDeal';
import capitalize from 'components/utils/capitalize';
import { formatRelative } from 'date-fns';
import firebaseToDate from 'components/utils/firebaseToDate';
import { useIntl } from 'react-intl';
import { ReloadOutlined, WarningOutlined } from '@ant-design/icons';
import { DraftOrder, getTriggerId, Order } from 'services/hitchit/types/online-sales/Order';
import { LinkedResource } from 'services/hitchit/types/hike/Resource';
import { Product } from 'services/hitchit/types/online-sales/Product';
import { TriggerCollapse } from 'components/hitches/online-store/TriggerCollapse';

const TRIGGER_DATA_QUERY = `triggerdata`;

const Triggers = ({ hike, limit }: { hike: Hike; limit?: number }): JSX.Element => {
  const { formatMessage } = useIntl();
  const { user } = useContext(UserAuthenticationContext);
  const [updates, setUpdates] = useState<Record<string, boolean>>({});
  const [filter, setFilter] = useState<'all' | 'products' | 'orders'>('all');
  const [showMore, setShowMore] = useState(false);

  const updateMessage = formatMessage({
    defaultMessage: 'Update',
    description: 'triggers/update/button',
  });

  // Queries
  const queryClient = useQueryClient();
  const query = useQuery<TriggerDataResponse | undefined, any>(
    [TRIGGER_DATA_QUERY, { id: hike.id, limit, user }],
    () => getTriggerData({ id: hike.id, limit, user })
  );

  const { componentToRender } = useQueryResult({ data: query.data, error: query.error });

  // New function to filter only 3 items of each type
  const filterByTypeLimit = (data: TriggerData[], limit: number) => {
    const typeCount: Record<string, number> = {};
    return data.filter((item) => {
      const type = item.trigger.type;
      if (!typeCount[type]) typeCount[type] = 0;
      if (typeCount[type] < limit) {
        typeCount[type]++;
        return true;
      }
      return false;
    });
  };

  let filteredData = query.data?.triggerData || [];
  if (filter === 'products') {
    filteredData = filteredData.filter((triggerData) => triggerData.trigger.type === 'product');
  } else if (filter === 'orders') {
    filteredData = filteredData.filter((triggerData) => triggerData.trigger.type !== 'product');
  }

  if (!showMore) {
    filteredData = filterByTypeLimit(filteredData, 3);
  }

  const update = async (trigger: Order | DraftOrder | Product) => {
    const triggerId = getTriggerId(trigger);
    // TODO what if no active user? log something
    if (user) {
      setUpdates({
        ...updates,
        [triggerId]: true,
      });
      // TODO add spinner or something?
      const { error } = await createHikeEntry(hike.id, triggerId, trigger.type, user);

      if (error?.message) {
        message.error(error.message);
      }
      await queryClient.invalidateQueries(TRIGGER_DATA_QUERY);
      setUpdates({
        ...updates,
        [triggerId]: false,
      });
    }
  };

  if (componentToRender) {
    return componentToRender;
  }

  if (query.data?.triggerData && query.data?.triggerData.length === 0) {
    return (
      <div>
        <p>
          <WarningOutlined style={{ marginRight: 20 }} />
          {formatMessage({
            defaultMessage: 'We cannot find any orders in your shop to try with.',
            description: 'triggers/no-orders/description',
          })}
        </p>
      </div>
    );
  }

  const allOrOnlyProducts = filter === 'all' || filter === 'products';
  const allOrOnlyOrders = filter === 'all' || filter === 'orders';

  return (
    <>
      <Button
        style={{
          margin: 32,
        }}
        onClick={() => (allOrOnlyProducts ? setFilter('orders') : setFilter('all'))}
      >
        {allOrOnlyProducts ? 'Show only orders' : 'Show all'}
      </Button>
      <Button
        style={{
          margin: 32,
        }}
        onClick={() => (allOrOnlyOrders ? setFilter('products') : setFilter('all'))}
      >
        {allOrOnlyOrders ? 'Show only products' : 'Show all'}
      </Button>
      <Timeline>
        {filteredData.map((triggerData, key) => {
          const linkedResources = triggerData?.resource
            ? triggerData.resource.linkedResources.filter(
                (linkedResource) => linkedResource.hitch === 'teamleader'
              )
            : [];

          const hasLinkedResources = linkedResources.length > 0;

          return (
            <Timeline.Item
              key={`${triggerData.trigger.createdAt}${key}`}
              color={hasLinkedResources ? 'green' : 'gray'}
            >
              <p>
                {capitalize(
                  formatRelative(
                    new Date(triggerData.trigger.updatedAt || triggerData.trigger.createdAt),
                    new Date()
                  )
                )}
                {hasLinkedResources && (
                  <Button
                    type="link"
                    icon={<ReloadOutlined key="retry" />}
                    onClick={() => update(triggerData.trigger)}
                  >
                    {formatMessage({
                      defaultMessage: 'Trigger again',
                      description: 'triggers/retry/description',
                    })}
                  </Button>
                )}
              </p>
              <Spin
                key={`${triggerData.trigger.createdAt}${key}`}
                spinning={!!updates[getTriggerId(triggerData.trigger)]}
              >
                <TriggerCollapse trigger={triggerData.trigger} />
                {/* linkedResources.length > 0 is needed, otherwise also new ones are indicated */}
                {linkedResources.length > 0 &&
                  !linkedResources.find(
                    (linkedResource) =>
                      (firebaseToDate(linkedResource.metadata?.updatedAt) as Date) >=
                      new Date(triggerData.trigger.updatedAt)
                  ) && (
                    <Col>
                      <Alert
                        type="warning"
                        message={
                          <Row gutter={32} align="middle" justify="center">
                            <Col>
                              {formatMessage({
                                defaultMessage:
                                  'Something changed since we last touched this entry. Do you want us to update it?',
                                description: 'triggers/update/description',
                              })}
                            </Col>
                            <Col>
                              <Button danger onClick={() => update(triggerData.trigger)}>
                                {updateMessage}
                              </Button>
                            </Col>
                          </Row>
                        }
                      />
                    </Col>
                  )}
                {/* linkedResources.length > 0 is needed, otherwise also new ones are indicated */}
                {linkedResources.length > 0 &&
                  !linkedResources.find(
                    (linkedResource) =>
                      linkedResource.metadata?.preferencesVersion === hike.preferencesVersion
                  ) && (
                    <Col>
                      <Alert
                        type="warning"
                        message={
                          <Row gutter={32} align="middle" justify="center">
                            <Col>
                              {formatMessage({
                                defaultMessage:
                                  'Created entry is out of sync with your current preferences',
                                description: 'triggers/out-of-sync/description',
                              })}
                            </Col>
                            <Col>
                              <Button danger onClick={() => update(triggerData.trigger)}>
                                {updateMessage}
                              </Button>
                            </Col>
                          </Row>
                        }
                      />
                    </Col>
                  )}

                <Row gutter={[32, 32]} style={{ padding: 32 }}>
                  {!hasLinkedResources && (
                    <Button
                      type="primary"
                      onClick={() => {
                        update(triggerData.trigger);
                      }}
                    >
                      {formatMessage({
                        defaultMessage: 'Create entry in Teamleader',
                        description: 'triggers/create/button',
                      })}
                    </Button>
                  )}
                  {/* remove duplicates */}
                  {/* add the newest one only from the duplicates: LinkedResource.metadata.updatedAt */}

                  {linkedResources
                    .reduce<LinkedResource[]>((currResources, linkedResource) => {
                      const duplicateResource = currResources.find((resource) => {
                        return resource.resource === linkedResource.resource;
                      });

                      const duplicateResourceDate = firebaseToDate(
                        duplicateResource?.metadata?.updatedAtLinkedResource
                      );

                      if (
                        duplicateResourceDate &&
                        // &&
                        // when the duplicate is older than the new one, remove it and add the new one
                        (firebaseToDate(linkedResource.metadata?.updatedAtLinkedResource) as Date) <
                          duplicateResourceDate
                      ) {
                        // don't add new one ;as it is an older duplicate
                      } else {
                        // remove the old one
                        currResources = currResources.filter((resource) => {
                          return resource.resource !== linkedResource.resource;
                        });
                        // and add the newer one
                        currResources.push(linkedResource);
                      }

                      return currResources;
                    }, [])
                    .map((linkedResource, index) => (
                      <a
                        key={`${linkedResource.url}${index}`}
                        target="_blank"
                        href={linkedResource.url}
                        rel="noreferrer"
                      >
                        <Button type="ghost">
                          {formatMessage(
                            {
                              defaultMessage: 'View {resource} in Teamleader',
                              description: 'triggers/view/output/link',
                            },
                            { resource: linkedResource.resource }
                          )}
                        </Button>
                      </a>
                    ))}
                </Row>
              </Spin>
            </Timeline.Item>
          );
        })}
      </Timeline>
      {filteredData.length < (query.data?.triggerData?.length || 0) && (
        <Button onClick={() => setShowMore(!showMore)}>
          {showMore ? 'Show Less' : 'View More'}
        </Button>
      )}
    </>
  );
};

export default Triggers;
